import { useState, useEffect } from 'react'
import { Modal } from '@/components/common'
import { directorApi } from '@/services/api/director'
import { ApiErrorResponse } from '@/services/api/client'
import type {
  DirectorActionItem, DirectorDecisionCreatePayload, DirectorRequestDetail,
} from '@/types/director'
import { extractErrorMessage, isDirectorDecisionConflict } from '@/utils/errorHandler'
import { Undo2 } from 'lucide-react'
import { lessonRange } from '@/components/effective-plan/planFormat'
import { sortLessonsByDateTime } from '@/utils/lessonSort'

interface QuickReturnModalProps {
  isOpen: boolean
  item: DirectorActionItem | null
  /** Lektionen aus dem Full-Detail — Checkboxen für zu entfernende Lektionen. */
  lessons: DirectorRequestDetail['lessons'] | null
  onClose: () => void
  onSuccess: () => void
  /** Fallakte v2 (K7): 409-Konflikt → Liste refreschen */
  onConflict?: () => void
}

function formatTableDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit', year: '2-digit' })
  } catch {
    return iso
  }
}

/** Zugewiesene Lektionen sind nicht entfernbar (V6/B.1-P5, Backend blockiert sie). */
const COMMITTED = ['accepted', 'assigned', 'filled', 'booked']

/** Fallakte v2 (P5.9): Anpassen / zurückstellen (returned) aus der Fallakte. */
export function QuickReturnModal({ isOpen, item, lessons, onClose, onSuccess, onConflict }: QuickReturnModalProps) {
  const [removedIds, setRemovedIds] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && item) {
      setRemovedIds([])
      setError(null)
    }
  }, [isOpen, item])

  if (!item) return null

  const rows = sortLessonsByDateTime(lessons ?? [])
  const selectable = rows.filter(l => !(l.assigned_teacher_id && COMMITTED.includes(l.status ?? '')))

  const toggleLesson = (lessonId: string) => {
    setRemovedIds(prev => prev.includes(lessonId)
      ? prev.filter(l => l !== lessonId)
      : [...prev, lessonId])
  }

  const handleSubmit = async () => {
    if (removedIds.length === 0) {
      setError('Bitte mindestens eine freie Lektion zum Entfernen auswählen.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const payload: DirectorDecisionCreatePayload = {
        substitution_request_id: item.id,
        payment_type: 'private',
        decision_type: 'returned',
        removed_lesson_ids: removedIds,
      }
      await directorApi.createDecision(payload)
      onSuccess()
    } catch (e: unknown) {
      let msg = extractErrorMessage(e, { loadError: 'Ein Fehler ist aufgetreten' })
      if (e instanceof ApiErrorResponse && e.statusCode === 403) {
        const serverDetail = (e.response?.data as { detail?: string } | undefined)?.detail
        if (serverDetail) msg = serverDetail
      }
      if (isDirectorDecisionConflict(e)) {
        setError('Diese Anfrage wurde bereits von einem anderen Direktor entschieden.')
        onConflict?.()
      } else {
        setError(msg)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Fall anpassen / zurückstellen" size="lg">
      <div className="space-y-4">
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-900">{item.requesting_teacher_name}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {item.lesson_count} Lektionen
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Zu entfernende Lektionen (mindestens eine freie)
          </label>
          <p className="text-xs text-gray-500 mb-1">
            {selectable.length} von {rows.length} Lektionen entfernbar —
            zugewiesene erfordern die Storno-Funktion.
          </p>
          <div className="overflow-x-auto rounded-md border border-gray-200 max-h-72 overflow-y-auto">
            <table className="w-full text-xs bg-white" aria-label="Lektionen der Anfrage">
              <thead className="text-[10px] text-gray-500 uppercase bg-gray-100 border-b sticky top-0">
                <tr>
                  <th scope="col" className="py-1.5 px-2 text-left w-8"></th>
                  <th scope="col" className="py-1.5 px-2 text-left font-medium">Datum</th>
                  <th scope="col" className="py-1.5 px-2 text-left font-medium">Zeit</th>
                  <th scope="col" className="py-1.5 px-2 text-left font-medium">Fach</th>
                  <th scope="col" className="py-1.5 px-2 text-left font-medium">Klasse</th>
                  <th scope="col" className="py-1.5 px-2 text-right font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(l => {
                  const isAssigned = Boolean(
                    l.assigned_teacher_id && COMMITTED.includes(l.status ?? ''),
                  )
                  const time = lessonRange(l, '-')
                  return (
                    <tr key={l.id} className={isAssigned ? 'opacity-60' : ''}>
                      <td className="py-1.5 px-2">
                        <input
                          type="checkbox"
                          checked={removedIds.includes(l.id)}
                          disabled={isAssigned}
                          onChange={() => toggleLesson(l.id)}
                          aria-label={`Lektion ${time ?? ''} ${l.subject_name ?? ''} am ${l.date ?? ''} auswählen`}
                          className="w-4 h-4"
                        />
                      </td>
                      <td className="py-1.5 px-2">{formatTableDate(l.date)}</td>
                      <td className="py-1.5 px-2 font-semibold">{time || '—'}</td>
                      <td className="py-1.5 px-2 font-medium">{l.subject_name || '—'}</td>
                      <td className="py-1.5 px-2">{l.klass_name || '—'}</td>
                      <td className="py-1.5 px-2 text-right">
                        {isAssigned
                          ? `✓ ${l.assigned_teacher_name || 'Vergeben'}`
                          : l.status === 'cancelled'
                            ? '✗ Storniert'
                            : '○ Offen'}
                      </td>
                    </tr>
                  )
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-3 px-2 text-center text-gray-400">
                      {submitting ? '' : 'Keine Lektionen geladen.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Die Anfrage wird zurückgestellt; die Lehrperson kann die Kaskade für den Rest neu auslösen
            oder sich per Chat einigen.
          </p>
        </div>

        {error && <div className="p-3 bg-red-100 text-red-700 rounded text-sm">{error}</div>}

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || selectable.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm font-medium"
          >
            <Undo2 className="w-4 h-4" />
            {submitting ? 'Speichern...' : 'Zurückstellen'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </Modal>
  )
}
